import React, { useState, useEffect, useCallback } from 'react'
import { X, Pencil } from 'lucide-react'
import {
  OrgChartService,
  RoleWithMember,
  RoleDefinition,
  Team
} from '../../services/orgChartService'
import { EditRoleDefinitionModal } from './EditRoleDefinitionModal'

interface CopiedRoleData {
  roleDefinitionId: string | null
  title: string
  teams: string[]
  teamColors: string[]
  reportsToRoleId: string | null
  accountabilities: string[]
  order: number
}

interface RoleModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: () => Promise<void> | void
  role: RoleWithMember | null // null = creating new card
  parentRoleId: string | null // for new cards
  orgChartId: string
  teams: Team[]
  roles: RoleWithMember[]
  roleDefinitions: RoleDefinition[]
  onRoleDefinitionsChanged: () => Promise<void> | void
  copiedRoleData?: CopiedRoleData | null
}

export const RoleModal: React.FC<RoleModalProps> = ({
  isOpen,
  onClose,
  onSave,
  role,
  parentRoleId,
  orgChartId,
  teams,
  roles,
  roleDefinitions,
  onRoleDefinitionsChanged,
  copiedRoleData
}) => {
  const [roleDefinitionId, setRoleDefinitionId] = useState<string | null>(null)
  const [selectedTeams, setSelectedTeams] = useState<string[]>([])
  const [reportsTo, setReportsTo] = useState<string | null>(null)
  const [order, setOrder] = useState(0)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [editDefinitionOpen, setEditDefinitionOpen] = useState(false)
  const [editingDefinition, setEditingDefinition] = useState<RoleDefinition | null>(null)

  const isEditing = !!role
  const orgChartService = OrgChartService.getInstance()

  const selectedDefinition =
    roleDefinitions.find(d => d.id === roleDefinitionId) || null

  const initForm = useCallback(() => {
    if (role) {
      setRoleDefinitionId(role.roleDefinitionId)
      setSelectedTeams(role.teams || [])
      setReportsTo(role.reportsToRoleId)
      setOrder(role.order ?? 0)
    } else if (copiedRoleData) {
      setRoleDefinitionId(copiedRoleData.roleDefinitionId)
      setSelectedTeams(copiedRoleData.teams || [])
      setReportsTo(copiedRoleData.reportsToRoleId)
      setOrder(copiedRoleData.order ?? 0)
    } else {
      setRoleDefinitionId(null)
      setSelectedTeams([])
      setReportsTo(parentRoleId)
      setOrder(0)
    }
    setError('')
  }, [role, parentRoleId, copiedRoleData])

  useEffect(() => {
    if (isOpen) initForm()
  }, [isOpen, initForm])

  const toggleTeam = (teamName: string) => {
    setSelectedTeams(prev =>
      prev.includes(teamName)
        ? prev.filter(t => t !== teamName)
        : [...prev, teamName]
    )
  }

  const openEditRole = () => {
    if (selectedDefinition) {
      setEditingDefinition(selectedDefinition)
    } else {
      setEditingDefinition(null)
    }
    setEditDefinitionOpen(true)
  }

  const openCreateRole = () => {
    setEditingDefinition(null)
    setEditDefinitionOpen(true)
  }

  const handleDefinitionSaved = async (definitionId: string | null) => {
    await onRoleDefinitionsChanged()
    setEditDefinitionOpen(false)
    setEditingDefinition(null)
    if (definitionId) {
      setRoleDefinitionId(definitionId)
    } else if (roleDefinitionId && !definitionId) {
      // Deleted current selection
      setRoleDefinitionId(null)
    }
  }

  const handleSave = async () => {
    if (!roleDefinitionId || !selectedDefinition) {
      setError('Select a role')
      return
    }

    try {
      setSaving(true)
      setError('')

      const teamColors = selectedTeams.map(
        teamName => teams.find(t => t.name === teamName)?.color || '#6b7280'
      )

      const roleData = {
        orgChartId: role?.orgChartId || orgChartId,
        roleDefinitionId,
        title: selectedDefinition.name,
        teams: selectedTeams,
        teamColors,
        reportsToRoleId: reportsTo,
        order: Number.isFinite(order) ? order : 0,
        accountabilities: selectedDefinition.accountabilities || [],
        assignedMemberId: role?.assignedMemberId || null,
        isContractor: !!selectedDefinition.isContractor
      }

      if (isEditing && role) {
        await orgChartService.updateRole(role.id, roleData)
      } else {
        await orgChartService.createRole(roleData)
      }

      await onSave()
    } catch (err: any) {
      console.error('Error saving role card:', err)
      setError(err.message || 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!role) return

    if (
      !confirm(
        'Are you sure you want to delete this org chart card? Any direct reports will become root cards. The role definition itself will not be deleted.'
      )
    ) {
      return
    }

    try {
      setSaving(true)
      await orgChartService.deleteRole(role.id)
      await onSave()
    } catch (err: any) {
      console.error('Error deleting role card:', err)
      setError(err.message || 'Failed to delete')
      setSaving(false)
    }
  }

  if (!isOpen) return null

  const availableParents = roles.filter(r => r.id !== role?.id)

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              {isEditing ? 'Edit Role Card' : 'Create Role Card'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-4 space-y-4">
            {error && (
              <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* Role selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role *
              </label>
              <div className="flex gap-2">
                <select
                  value={roleDefinitionId || ''}
                  onChange={(e) => setRoleDefinitionId(e.target.value || null)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">Select a role...</option>
                  {roleDefinitions.map(def => (
                    <option key={def.id} value={def.id}>
                      {def.name}{def.isContractor ? ' (Contractor)' : ''}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={openEditRole}
                  disabled={!selectedDefinition}
                  title={
                    selectedDefinition
                      ? 'Edit role (name, accountabilities, contractor)'
                      : 'Select a role first, or create a new one'
                  }
                  className="inline-flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-sm whitespace-nowrap"
                >
                  <Pencil className="w-4 h-4" />
                  Edit Role
                </button>
              </div>
              <button
                type="button"
                onClick={openCreateRole}
                className="mt-2 text-sm text-purple-600 hover:text-purple-700"
              >
                + Create new role
              </button>
            </div>

            {/* Accountabilities (from selected role) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Accountabilities
              </label>
              {!selectedDefinition ? (
                <p className="text-sm text-gray-500 p-3 bg-gray-50 rounded-lg">
                  Select a role to see its accountabilities.
                </p>
              ) : selectedDefinition.accountabilities?.length > 0 ? (
                <ul className="space-y-1 border border-gray-200 rounded-lg p-3 bg-gray-50">
                  {selectedDefinition.accountabilities.map((acc, idx) => (
                    <li key={idx} className="text-sm text-gray-700">
                      • {acc}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-gray-500 p-3 bg-gray-50 rounded-lg">
                  This role has no accountabilities yet. Use Edit Role to add them.
                </p>
              )}
            </div>

            {/* Teams (multi-select) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Teams
              </label>
              {teams.length > 0 ? (
                <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2">
                  {teams.map(team => (
                    <label
                      key={team.id}
                      className="flex items-center space-x-3 p-2 rounded hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedTeams.includes(team.name)}
                        onChange={() => toggleTeam(team.name)}
                        className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                      <div
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: team.color }}
                      />
                      <span className="text-sm text-gray-700">{team.name}</span>
                    </label>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 p-2 bg-gray-50 rounded-lg">
                  No teams defined yet. Create teams in the Teams view.
                </p>
              )}
              {selectedTeams.length === 0 && teams.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  No teams selected - will inherit from parent role
                </p>
              )}
              {selectedTeams.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {selectedTeams.map(teamName => {
                    const team = teams.find(t => t.name === teamName)
                    return (
                      <span
                        key={teamName}
                        className="px-2 py-1 text-xs rounded-full text-white"
                        style={{ backgroundColor: team?.color || '#6b7280' }}
                      >
                        {teamName}
                      </span>
                    )
                  })}
                </div>
              )}
            </div>

            {/* Reports To */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reports To
              </label>
              <select
                value={reportsTo || ''}
                onChange={(e) => setReportsTo(e.target.value || null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="">No one (top of org chart)</option>
                {availableParents.map(r => (
                  <option key={r.id} value={r.id}>
                    {r.title}{' '}
                    {r.assignedMember
                      ? `(${r.assignedMember.name.fullName})`
                      : '(Vacant)'}
                  </option>
                ))}
              </select>
            </div>

            {/* Peer order */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Order
              </label>
              <input
                type="number"
                value={order}
                onChange={(e) => setOrder(parseInt(e.target.value, 10) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
            <div>
              {isEditing && (
                <button
                  onClick={handleDelete}
                  disabled={saving}
                  className="px-4 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors text-sm"
                >
                  Delete Card
                </button>
              )}
            </div>
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Card'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {editDefinitionOpen && (
        <EditRoleDefinitionModal
          isOpen={editDefinitionOpen}
          onClose={() => {
            setEditDefinitionOpen(false)
            setEditingDefinition(null)
          }}
          onSaved={handleDefinitionSaved}
          definition={editingDefinition}
        />
      )}
    </>
  )
}
